import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import _ from 'lodash';
import axios from 'axios';
import { AppCard, AppRemixIcon } from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import AppSpinner from '@/layout/components/AppSpinner';
import SurveyBase from './SurveyBase';
import SurveySidebar from '../../../layout/screenSpecifics/Modules/Survey/Sidebar/SurveySidebar';
import SurveyQuestion from '../../../layout/screenSpecifics/Modules/Survey/SurveyQuestion';

export default function SurveyHomeScreen() {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const surveyUser = useSelector((state) => state.currentPage?.surveyUser);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD SURVEY
  const loadSurveyUser = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_survey_get'), {
          moduleBridgeHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('surveyUser', data));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex],
  );

  useEffect(() => {
    loadSurveyUser();
  }, []);
  // < LOAD SURVEY

  // > START SURVEY
  const handleStart = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    return axios
      .post(axiosPath('api_r_user_module_survey_start'), {
        moduleBridgeHex,
      })
      .then(() => {
        if (isMounted.current) {
          loadSurveyUser();
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, isLoading]);
  // < START SURVEY

  // > END SURVEY
  const handleEnd = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    return axios
      .post(axiosPath('api_r_user_module_survey_end'), {
        moduleBridgeHex,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          const updatedSurveyUser = { ...surveyUser };
          updatedSurveyUser.dateEnd = data?.dateEnd;
          dispatch(currentPageAction.change('surveyUser', updatedSurveyUser, true));
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, isLoading]);
  // < END SURVEY

  return (
    <>
      <SurveyBase />
      <SurveySidebar activeRoute="user_module_survey_home" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [{ name: moduleData?.moduleName }, { name: 'Inicio' }]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {surveyUser ? (
            <>
              {surveyUser?.dateEnd ? (
                <CenterContainer>
                  <div className="alert alert-success">Encuesta finalizada</div>
                </CenterContainer>
              ) : (
                <>
                  {surveyUser?.dateStart ? (
                    <AppCard>
                      {surveyUser?.questions?.map((q) => (
                        <SurveyQuestion question={q} key={q?.id} />
                      ))}
                      <CenterContainer>
                        <button
                          type="button"
                          className="btn btn-success d-flex mt-3"
                          onClick={handleEnd}
                          disabled={isLoading}
                        >
                          Finalizar encuesta{' '}
                          {isLoading && (
                            <span className="ml-2">
                              <AppSpinner>
                                <AppRemixIcon icon="loader-4" size={16} />
                              </AppSpinner>
                            </span>
                          )}
                        </button>
                      </CenterContainer>
                    </AppCard>
                  ) : (
                    <CenterContainer>
                      <button
                        type="button"
                        className="btn btn-success d-flex"
                        onClick={handleStart}
                        disabled={isLoading}
                      >
                        Iniciar encuesta{' '}
                        {isLoading && (
                          <span className="ml-2">
                            <AppSpinner>
                              <AppRemixIcon icon="loader-4" size={16} />
                            </AppSpinner>
                          </span>
                        )}
                      </button>
                    </CenterContainer>
                  )}
                </>
              )}
            </>
          ) : (
            <AppSpinner>
              <AppRemixIcon icon="loader-4" size={40} />
            </AppSpinner>
          )}
        </div>
      </main>
    </>
  );
}

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;